import type { RegisterSession } from '../types';
import { toMillis } from './orderTimestamps';

/** Whether a register session should be treated as open (matches POS guard semantics). */
export function isRegisterSessionActive(session: RegisterSession | null | undefined): boolean {
  if (!session) return false;
  if (session.isActive === false) return false;
  const endMs = toMillis(session.endTime);
  if (endMs > 0) return false;
  return true;
}

/** Coerce Firestore Timestamp / seconds / ms fields on a register session row. */
export function normalizeRegisterSession(session: RegisterSession): RegisterSession {
  const startTime = toMillis(session.startTime);
  const endTime = session.endTime != null ? toMillis(session.endTime) : undefined;
  const lastReminderAt =
    session.lastReminderAt != null ? toMillis(session.lastReminderAt) : undefined;
  return {
    ...session,
    startTime: startTime > 0 ? startTime : session.startTime,
    endTime: endTime && endTime > 0 ? endTime : undefined,
    lastReminderAt: lastReminderAt && lastReminderAt > 0 ? lastReminderAt : undefined,
  };
}

function registerSessionCompletenessScore(session: RegisterSession): number {
  const s = normalizeRegisterSession(session);
  let score = 0;
  if (toMillis(s.startTime) > 0) score += 10;
  if (s.cashierName?.trim()) score += 5;
  if (s.terminalName?.trim() || s.terminalId) score += 3;
  if (s.openingFloat != null) score += 2;
  if (s.cashierId != null) score += 1;
  return score;
}

/** Merge two snapshots of the same session id, keeping the richest field values. */
export function mergeRegisterSessionRecords(
  a: RegisterSession,
  b: RegisterSession,
): RegisterSession {
  const na = normalizeRegisterSession(a);
  const nb = normalizeRegisterSession(b);
  const primary =
    registerSessionCompletenessScore(na) >= registerSessionCompletenessScore(nb) ? na : nb;
  const secondary = primary === na ? nb : na;
  return normalizeRegisterSession({
    ...secondary,
    ...primary,
    id: primary.id || secondary.id,
    startTime: toMillis(primary.startTime) || toMillis(secondary.startTime),
    cashierName: primary.cashierName || secondary.cashierName,
    cashierId: primary.cashierId ?? secondary.cashierId,
    cashierEmail: primary.cashierEmail || secondary.cashierEmail,
    terminalName: primary.terminalName || secondary.terminalName,
    terminalId: primary.terminalId || secondary.terminalId,
    openingFloat: primary.openingFloat ?? secondary.openingFloat,
    lastReminderAt:
      toMillis(primary.lastReminderAt) || toMillis(secondary.lastReminderAt) || undefined,
    isActive: isRegisterSessionActive(primary) || isRegisterSessionActive(secondary),
  });
}

export function registerSessionStartMs(session: RegisterSession): number {
  return toMillis(normalizeRegisterSession(session).startTime);
}

/** Collect active register sessions from several sources, merging duplicates by id. */
export function collectActiveRegisterSessions(
  ...sources: Array<RegisterSession | null | undefined | RegisterSession[]>
): RegisterSession[] {
  const map = new Map<string, RegisterSession>();
  const add = (session: RegisterSession | null | undefined) => {
    if (!session || !session.id || !isRegisterSessionActive(session)) return;
    const existing = map.get(session.id);
    map.set(
      session.id,
      existing ? mergeRegisterSessionRecords(existing, session) : normalizeRegisterSession(session),
    );
  };
  for (const source of sources) {
    if (Array.isArray(source)) {
      for (const session of source) add(session);
    } else {
      add(source);
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => registerSessionStartMs(b) - registerSessionStartMs(a),
  );
}
